import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import lround from "lodash/round";
import cloneDeep from "lodash/cloneDeep";
import convert from "convert-units";
import submit from "submit-form";
import { SearchDropdown } from "../../inputs/search-dropdown";
import AddSetRow from "./AddSetRow";
import AddExerciseSets from "./AddExerciseSets";
import {
  prevalidateSet,
  validateComplexExercises,
  validateComplexSet,
} from "../../../utils/exercise-validations";
import { resetInputs } from "../../../utils/dom-helpers";
import EditSetRow from "./EditSetRow";
import { getComplexExerciseName, getComplexSets } from "../../../utils/complex";
import { useRillContext } from "../../../rill/RillReact/share/base";
import {
  ADD_EXERCISE_PRIMARY_ACTION_ID,
  focusAfterSetCommit,
  preventImplicitEnterSubmit,
} from "./set-entry-keyboard";

const AddComplex = ({ canAccessLibrary, date }) => {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const { locals } = useRillContext();

  const { complexes = [], activeUserToRequest, athlete, query } = locals;
  const { editMode, complex } = query;
  const options = complexes.map((complex) => ({
    ...complex,
    label: complex.name,
    value: complex.name,
  }));
  const [selectedComplex, setSelectedComplex] = useState(
    options.find(({ _id }) => _id === complex) || null
  );
  const [newSet, setNewSet] = useState({
    units: {
      load: locals.activeUser.units,
    },
  });
  const [editSet, setEditSet] = useState({
    units: {
      load: locals.activeUser.units,
    },
  });
  const [editSetBackup, setEditSetBackup] = useState(null);
  const [isNewSetValid, setIsNewSetValid] = useState(false);
  const [sets, setSets] = useState(
    getComplexSets(
      locals.activeExercises || locals.editExercises,
      query.exercises
    )
  );
  const [notes, setNotes] = useState("");
  const [preferredLoadInput, setPreferredLoadInput] = useState(
    activeUserToRequest.preferredLoadInput || "percent"
  );

  const repMax = useMemo(() => {
    if (!selectedComplex) return 100;
    const user = athlete || activeUserToRequest;
    const { exerciseLibrary, repMaximums } = user;
    const exercise =
      selectedComplex.exercises[selectedComplex.loadBasedOn ?? 0];
    if (!exercise?.movement) {
      return repMaximums[exercise.primary] || 100;
    }
    const libraryExercise = (exerciseLibrary[exercise.primary] || []).find(
      (ex) => ex.movement === exercise.movement
    );
    return libraryExercise?.repMaximum || repMaximums[exercise.primary] || 100;
  }, [selectedComplex, activeUserToRequest, athlete]);

  const handleSetChange = (e, isEditSet = false) => {
    const name = e.target.name;
    const currentSet = isEditSet ? { ...editSet } : { ...newSet };
    currentSet[name] = e.target.value;

    if (["sets"].includes(name)) {
      currentSet[name] = Math.round(e.target.value);
      e.target.value = currentSet[name];
    }

    // Forcing user to not input more than two decimals on load or repMax
    if (["load", "repMaximum"].includes(name)) {
      const value = String(e.target.value);
      if (
        value.includes(".") &&
        !value.endsWith(".") &&
        value.split(".")[1].length > 2
      ) {
        e.target.value = lround(parseFloat(value), 2);
      }
    }

    if (["load", "percentRepMax", "loadDrop"].indexOf(name) > -1) {
      if (String(currentSet[name]).includes(",")) {
        currentSet[name] = e.target.value = String(currentSet[name]).replace(
          /,/g,
          "."
        );
      }
    }

    if (["load"].indexOf(name) > -1 && "metric" !== currentSet.units[name]) {
      currentSet[name] = convert(currentSet[name]).from("lb").to("kg");
    }

    // Remembers last value (weight or percent) and converts appropriately during toggle
    if (name === "percentRepMax") {
      currentSet.load = (repMax * parseFloat(currentSet[name])) / 100;
      // Ensure display load is converted to the most recently selected unit when switching from percent
      document.getElementById(
        isEditSet ? "exerciseEditWeightLoad" : "exerciseAddWeightLoad"
      ).value = lround(
        currentSet.units["load"] === "metric"
          ? currentSet.load
          : convert(currentSet.load).from("kg").to("lb"),
        2
      );
    }

    if (name === "load") {
      // No conversion needed since both defaultRepMax and load are ensured to be in KG
      currentSet.percentRepMax = (parseFloat(currentSet.load) / repMax) * 100;
      document.getElementById(
        isEditSet ? "exerciseEditLoad" : "exerciseAddLoad"
      ).value = lround(currentSet.percentRepMax, 2);
    }

    if (isEditSet) {
      setEditSet(currentSet);
    } else {
      setNewSet(currentSet);
      const requireRepsTwo = !!selectedComplex;
      const requireRepsThree = selectedComplex?.exercises?.length > 2;
      setIsNewSetValid(
        prevalidateSet(currentSet, false, requireRepsTwo, requireRepsThree)
      );
    }
  };

  const handleAddSet = (e) => {
    e.preventDefault();
    const { exercises } = selectedComplex;
    const exercise = exercises[selectedComplex.loadBasedOn ?? 0] || {};
    const { primary, movement } = exercise;
    const requireRepsTwo = !!selectedComplex;
    const requireRepsThree = selectedComplex?.exercises?.length > 2;
    const setToAdd = validateComplexSet({
      exercise: {
        primary,
        secondary: movement,
        type: movement ? "secondary" : "primary",
      },
      set: newSet,
      repMax,
      locals,
      requireRepsTwo,
      requireRepsThree,
    });

    if (setToAdd) {
      setSets((prevSets) => [...prevSets, setToAdd]);
      setNewSet({ units: newSet.units });
      setIsNewSetValid(false);

      resetInputs();
      focusAfterSetCommit(
        e.focusAfterCommit === "primary" ? "primary" : "sets"
      );
    }
  };

  const handleUpdateSet = (setIndex) => {
    const { exercises } = selectedComplex;
    const exercise = exercises[selectedComplex.loadBasedOn ?? 0] || {};
    const { primary, movement } = exercise;
    const requireRepsTwo = !!selectedComplex;
    const requireRepsThree = selectedComplex?.exercises?.length > 2;
    const setToEdit = validateComplexSet({
      exercise: {
        primary,
        secondary: movement,
        type: movement ? "secondary" : "primary",
      },
      set: editSet,
      repMax,
      locals,
      requireRepsTwo,
      requireRepsThree,
    });

    if (setToEdit) {
      const updatedSets = [...sets];
      updatedSets[setIndex] = setToEdit;
      updatedSets[setIndex].editMode = false;
      setSets(updatedSets);
    }
    // re-focuses on set input
    if (document.getElementById("exerciseAddSets"))
      document.getElementById("exerciseAddSets").focus();
  };

  const handleUnitChange = (e, isEditSet) => {
    let {
      target: { name, value },
    } = e;
    const currentSet = isEditSet ? { ...editSet } : { ...newSet };
    if (!currentSet.units)
      currentSet.units = {
        load: activeUserToRequest.units,
      };
    name = name.split("[")[1].split("]")[0];

    // Enforce load to be the same as the user's defaults
    if (name === "load") {
      const currentUnits = currentSet.units[name] === "imperial" ? "lb" : "kg";
      if (currentSet.units[name] !== value) {
        // Only update if there is a change
        const newUnits = currentUnits === "lb" ? "kg" : "lb";
        const inputId = isEditSet
          ? "exerciseEditWeightLoad"
          : "exerciseAddWeightLoad";

        const newValue = lround(
          convert(document.getElementById(inputId).value)
            .from(currentUnits)
            .to(newUnits),
          2
        );
        document.getElementById(inputId).value = newValue;
      }
    }

    currentSet.units[name] = value;
    if (!isEditSet) setNewSet(currentSet);
    else setEditSet(currentSet);
  };

  const handleReorderSet = (direction, index) => {
    const newSets = [...sets];
    const setToMove = newSets[index];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newSets.length) return;
    newSets[index] = newSets[newIndex];
    newSets[newIndex] = setToMove;
    setSets(newSets);
  };

  const handleRemoveSet = (index) => {
    const newSets = [...sets];
    newSets.splice(index, 1);
    setSets(newSets);
  };

  const handleSetEditModeToggle = (editSetIndex, initialSetState) => {
    const newSets = [...sets];
    newSets.forEach((set, index) => {
      if (index === editSetIndex || set.editMode === true)
        newSets[index].editMode = !set.editMode;
      if (index === editSetIndex && initialSetState)
        newSets[editSetIndex] = initialSetState;
    });
    // backing up editSet
    const editSetBackup = initialSetState
      ? { ...editSet }
      : { ...newSets[editSetIndex] };
    editSetBackup.editMode = false;
    const updatedEditSet = {
      units: {
        load: locals.activeUser.units,
      },
      ...newSets[editSetIndex],
      idx: editSetIndex,
    };

    setSets(newSets);
    setEditSet(updatedEditSet);
    setEditSetBackup(editSetBackup);

    if (document.getElementById("exerciseAddSets"))
      document.getElementById("exerciseAddSets").focus();
  };

  const handleSubmit = (e) => {
    // Post all form data
    e.preventDefault();

    const form = { ...selectedComplex, notes, sets };
    if (locals.params?.cycleId || locals.query?.cycle) {
      form.cycle = {
        id: locals.params?.cycleId || locals.query.cycle,
        isScheduled: false,
        program: locals.query.program,
        programWeek: locals.query.programWeek || locals.query.week,
        programSession:
          locals.query.programSession ||
          (parseInt(locals.query.session)
            ? locals.query.session
            : locals.query.session?.order),
      };
    }
    if (date) {
      delete form.cycle;
      form.date = date.clone().toDate();
      form.utcDate = parseInt(date.clone().format("YYYYMMDD"), 10);
    }

    const exercises = validateComplexExercises(form, repMax, locals);

    if (exercises?.length) {
      submit(`/actions/complex/save?proxyAthlete=${locals.query.athlete}`, {
        method: "POST",
        body: {
          exercises,
          organizationCoach: activeUserToRequest?._id,
          exercisesToEdit: query.exercises,
        },
      });
    }
  };

  const intensityMeasure =
    (activeUserToRequest?.intensityMeasure || "rpe") === "rpe" ? "RPE" : "RiR";
  const isRiR = intensityMeasure === "RiR";
  const isEditing = sets.some((set) => set.editMode);

  return (
    <form
      onSubmit={handleSubmit}
      onKeyDown={preventImplicitEnterSubmit}
      id="add-complex"
    >
      <div className="body">
        <div className="form-row">
          <div className="form-row-item">
            <label>{t("addComplex.selectComplex")}</label>
            <SearchDropdown
              options={options}
              placeholder={t("addComplex.selectComplex")}
              handleChange={(val) => {
                if (val) {
                  setSelectedComplex(val);
                }
              }}
              value={selectedComplex?.value}
              disabled={editMode}
            />
          </div>
        </div>

        <div className="group alt-add-sets">
          <AddExerciseSets
            primary="complex"
            sets={sets}
            locals={locals}
            handleReorderSet={handleReorderSet}
            handleSetEditModeToggle={handleSetEditModeToggle}
            handleRemoveSet={handleRemoveSet}
            editSetBackup={editSetBackup}
            intensityMeasure={intensityMeasure}
            isRiR={isRiR}
            exerciseType="primary"
            activeUser={activeUserToRequest}
            isEditing={false}
            activeUserToRequest={activeUserToRequest}
            defaultRepMax={repMax}
          />

          {isEditing ? (
            <EditSetRow
              editSet={cloneDeep(editSet)}
              defaultRepMax={repMax}
              form={{}}
              formSets={cloneDeep(sets)}
              setIndex={editSet.idx}
              handleEditSetChange={(e) => handleSetChange(e, true)}
              handleUpdateSet={handleUpdateSet}
              handleUnitChange={handleUnitChange}
              isAssistant={false}
              activeUser={locals.activeUser}
              preferredLoadInput={preferredLoadInput}
              setPreferredLoadInput={(value) => {
                setPreferredLoadInput(value);
                setEditSet((editSet) => {
                  if (value === "drop") {
                    editSet.load = 0;
                    editSet.percentRepMax = 0;
                  }
                  return editSet;
                });
              }}
              primary="complex"
              complexLength={selectedComplex?.exercises?.length}
            />
          ) : (
            <AddSetRow
              primary="complex"
              handleNewSetChange={handleSetChange}
              isRiR={isRiR}
              intensityMeasure={intensityMeasure}
              handleUnitChange={handleUnitChange}
              newSet={newSet}
              preferredLoadInput={preferredLoadInput}
              setPreferredLoadInput={(value) => {
                setPreferredLoadInput(value);
                setNewSet((newSet) => {
                  if (value === "drop") {
                    newSet.load = 0;
                    newSet.percentRepMax = 0;
                  }
                  return newSet;
                });
              }}
              isNewSetValid={isNewSetValid && !!selectedComplex}
              handleAddSet={handleAddSet}
              handleParameterInputChange={() => {}}
              activeUser={locals.activeUser}
              complexLength={selectedComplex?.exercises?.length}
            />
          )}

          {selectedComplex?.exercises[selectedComplex?.loadBasedOn ?? 0] && (
            <p className="p5 mt-4">
              {t("addComplex.loadBasedOn")}{" "}
              <span className="text-(--white) font-semibold">
                {getComplexExerciseName(selectedComplex)}
              </span>
            </p>
          )}
        </div>

        <div className="form-row">
          <div className="form-row-item">
            <label htmlFor="notes">{t("common:notes")}</label>
            <textarea
              tabIndex="0"
              name="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("addMetcon.notesPlaceholder")}
            />
          </div>
        </div>
      </div>

      <footer>
        {/* {canAccessLibrary ? (
          <a
            tabIndex="0"
            role="button"
            data-button-type="secondary"
            href="/actions/refresh?modal[type]=add-complex"
          >
            Create new complex
          </a>
        ) : ( */}
        <div className="invisible" />
        {/* )} */}

        <div className="flex gap-2">
          <button
            data-button-type="secondary"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              submit(`/actions/refresh/`, {
                method: "GET",
                body: { modal: "", editMode: "", addType: "" },
              });
            }}
            className="text-(--danger)!"
          >
            {t("common:cancel")}
          </button>
          <button
            id={ADD_EXERCISE_PRIMARY_ACTION_ID}
            data-button-type="primary"
            type="button"
            onClick={handleSubmit}
            disabled={!sets.length}
          >
            {t("common:save")}
          </button>
        </div>
      </footer>
    </form>
  );
};

export default AddComplex;
